import { useState, useEffect, useRef } from 'react';
import { useApp } from '../App';
import api from '../api';
import Loading from '../components/Loading';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const DOC_LABELS = {
  passport: '🛂 Паспорт', visa: '📋 Виза', pesel_card: '🆔 PESEL',
  zameldowanie: '🏠 Замельдование', contract: '📃 Контракт',
  karta_pobytu: '🛂 Karta Pobytu', other: '📎 Другое',
};
const STATUS_LABELS = { draft: '📝 Черновик', verified: '✅ Подтверждён', replaced: '🔄 Заменён' };

export default function Documents() {
  const { user, setUser, setPage } = useApp();
  const { t } = useI18n();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('');
  const [msg, setMsg] = useState('');
  const fileRef = useRef();
  const scanRef = useRef();

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const { data } = await api.get('/documents');
      setDocs(data);
    } catch {}
    setLoading(false);
  }

  // ─── Passport OCR ───────────────────────────
  async function handleScanPassport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true); setMsg(''); setScanResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/documents/ocr-passport', fd);
      setScanResult(data);
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || t('documents.recognition_error')));
    }
    setScanning(false);
  }

  async function applyToProfile() {
    if (!scanResult?.document_id) return;
    setMsg('');
    try {
      const { data } = await api.post(`/documents/${scanResult.document_id}/apply-to-profile`);
      setMsg(`✅ ${data.message}`);
      setScanResult(null);
      load();
      // Refresh user profile
      try {
        const { data: me } = await api.get('/me');
        setUser(me);
      } catch {}
    } catch (e) { setMsg('❌ ' + (e.response?.data?.detail || e.message)); }
  }

  // ─── Generic upload ─────────────────────────
  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file || !uploadType) return;
    setUploading(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', uploadType);
      await api.post('/documents', fd);
      setMsg('✅ Документ загружен');
      setUploadType('');
      load();
    } catch (e) { setMsg('❌ ' + (e.response?.data?.detail || e.message)); }
    setUploading(false);
  }

  if (loading) return <Loading />;

  // ─── Scan result preview ────────────────────
  if (scanResult) {
    const d = scanResult.extracted;
    const conf = scanResult.confidence || {};
    const lowFields = scanResult.low_confidence_fields || [];

    return (
      <div className="page">
        <h1>🔍 Результат сканирования</h1>
        {scanResult.warnings?.length > 0 && (
          <div className="banner banner-warning">{scanResult.warnings.join(' • ')}</div>
        )}
        <div className="form">
          {[
            ['first_name', 'Имя'], ['last_name', 'Фамилия'],
            ['date_of_birth', 'Дата рождения'], ['citizenship', 'Гражданство'],
            ['passport_number', 'Номер паспорта'], ['passport_expiry', 'Действителен до'],
            ['sex', 'Пол'], ['place_of_birth', 'Место рождения'],
          ].map(([k, label]) => (
            <div key={k} className={`field ${lowFields.includes(k) ? 'field-warning' : ''}`}>
              <label>{label} {conf[k] && <span className={`confidence conf-${conf[k]}`}>{conf[k]}</span>}</label>
              <input
                value={d[k] || ''}
                onChange={e => setScanResult({
                  ...scanResult,
                  extracted: { ...d, [k]: e.target.value },
                })}
              />
            </div>
          ))}
        </div>
        {lowFields.length > 0 && (
          <div className="banner banner-warning">
            ⚠️ Поля с низкой уверенностью: {lowFields.join(', ')} — проверьте вручную
          </div>
        )}
        <div className="actions">
          <button className="btn btn-primary" onClick={applyToProfile}>
            ✅ Заполнить профиль этими данными
          </button>
          <button className="btn btn-secondary" onClick={() => { setScanResult(null); scanRef.current && (scanRef.current.value = ''); }}>
            🔄 Сканировать заново
          </button>
          <button className="btn btn-link" onClick={() => setScanResult(null)}>{t('common.cancel')}</button>
        </div>
        {msg && <div className="message">{msg}</div>}
      </div>
    );
  }

  // ─── Main view ──────────────────────────────
  return (
    <div className="page">
      <BackButton />
      <h1>{t('documents.title')}</h1>

      {/* Passport scan card */}
      <div className="card card-action" onClick={() => scanRef.current?.click()}>
        <div className="card-icon">📷</div>
        <div className="card-body">
          <div className="card-title">Скан паспорта</div>
          <div className="card-desc">Сделайте фото → автозаполнение профиля</div>
        </div>
        {scanning && <div className="spinner" />}
      </div>
      <input ref={scanRef} type="file" accept="image/*" capture="environment"
        style={{display:'none'}} onChange={handleScanPassport} />

      {/* Upload other document */}
      <div className="detail-card" style={{marginTop:12}}>
        <h3 style={{margin:'0 0 8px'}}>📎 Загрузить документ</h3>
        <select value={uploadType} onChange={e => setUploadType(e.target.value)}
          style={{width:'100%', padding:8, marginBottom:8, borderRadius:8, background:'var(--card-bg)', color:'var(--text1)', border:'1px solid var(--border)'}}>
          <option value="">Выберите тип...</option>
          {Object.entries(DOC_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {uploadType && (
          <>
            <input ref={fileRef} type="file" accept="image/*,application/pdf"
              style={{display:'none'}} onChange={handleUpload} />
            <button className="btn btn-secondary" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? '...' : '📤 Выбрать файл'}
            </button>
          </>
        )}
      </div>

      {msg && <div className="message" style={{marginTop:8}}>{msg}</div>}

      {/* Document list */}
      <h2 className="section-title" style={{marginTop:16}}>{t('documents.my_documents')}</h2>
      {docs.length === 0 ? (
        <div className="empty">{t('documents.no_documents')}</div>
      ) : (
        <div className="list">
          {docs.map(d => (
            <div key={d.id} className="list-item"
              onClick={() => window.open(`https://api.workifypl.com/documents/${d.id}/file`, '_blank')}>
              <div className="list-main">
                <div className="list-title">{DOC_LABELS[d.doc_type] || d.doc_type}</div>
                <div className="list-sub">{d.file_name} • {d.created_at?.split('T')[0]}</div>
              </div>
              <div className="list-right">
                <span className={`status-badge status-${d.status}`}>{STATUS_LABELS[d.status] || d.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
